(() => {
  if (window.KindraWelcomePopup) return;
  window.KindraWelcomePopup = class {
    constructor(tray) {
      this.tray = tray;
      this.abort = new AbortController();
      this.copy = document.getElementById('kindra-welcome-copy');
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.updateViewport = () => { this.position(); if (this.editorOpen) this.sync(); };
      window.addEventListener('resize', this.updateViewport, { passive: true, signal: this.abort.signal });
      window.visualViewport?.addEventListener('resize', this.updateViewport, { passive: true, signal: this.abort.signal });
      window.visualViewport?.addEventListener('scroll', this.updateViewport, { passive: true, signal: this.abort.signal });
      window.addEventListener('keydown', event => {
        if (event.key === 'Escape' && this.panel?.isConnected && !this.allowClose) {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.close();
        }
      }, { capture: true, signal: this.abort.signal });
    }
    attach(host) {
      if (this.host === host) return;
      this.hostAbort?.abort();
      this.hostAbort = new AbortController();
      this.host = host;
      this.panel = null;
      host.shadowRoot.addEventListener('click', event => {
        if (this.allowClose) return;
        const target = event.target;
        const close = target.closest?.('[aria-label="Close modal"]');
        const backdrop = target.matches?.('[class*="_overlayBackground_"]');
        if (close || backdrop) {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.close();
        }
      }, { capture: true, signal: this.hostAbort.signal });
      this.position();
    }
    async openEditor(trigger) {
      if (!this.tray.editorPreview || this.editorOpen || this.editorOpening) return;
      const template = document.getElementById('kindra-welcome-editor');
      if (!template) return;
      this.editorOpening = true;
      if (!this.editorHost) {
        this.editorHost = document.createElement('div');
        this.editorHost.setAttribute('data-kindra-editor-popup', '');
        const shadow = this.editorHost.attachShadow({ mode: 'open' });
        shadow.append(document.getElementById('kindra-welcome-form-style').content.cloneNode(true), template.content.cloneNode(true));
        document.body.append(this.editorHost);
        const stylesheet = shadow.querySelector('link');
        if (!stylesheet.sheet) await new Promise(resolve => {
          stylesheet.addEventListener('load', resolve, { once: true });
          stylesheet.addEventListener('error', resolve, { once: true });
        });
        if (this.abort.signal.aborted) return;
        shadow.querySelector('form').addEventListener('submit', event => event.preventDefault(), { signal: this.abort.signal });
        shadow.querySelector('dialog').addEventListener('cancel', event => { event.preventDefault(); this.close(); }, { signal: this.abort.signal });
        shadow.querySelector('dialog').addEventListener('click', event => {
          if (event.target !== event.currentTarget) return;
          const box = event.currentTarget.getBoundingClientRect();
          if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) this.close();
        }, { signal: this.abort.signal });
      }
      this.attach(this.editorHost);
      this.prepare(trigger);
      this.editorOpen = true;
      this.editorOpening = false;
      this.editorHost.shadowRoot.querySelector('dialog').showModal();
      this.sync();
      this.tray.schedule();
    }
    prepare(trigger) {
      const source = trigger?.closest?.('[data-ke-offer], [data-welcome-offer], [data-welcome-orb]') || this.tray;
      this.origin = source?.getBoundingClientRect();
      this.centered = !!trigger?.closest?.('kindra-elata-product') || this.tray.hasAttribute('data-product');
      this.position();
    }
    position() {
      if (!this.host) return;
      const viewport = window.visualViewport;
      const mobile = window.matchMedia('(max-width: 767px)').matches;
      const gap = mobile ? 12 : 24;
      const width = Math.min(mobile ? 420 : 480, (viewport?.width || innerWidth) - gap * 2);
      const left = mobile || this.centered ? Math.max(gap, (innerWidth - width) / 2) : gap;
      const keyboard = Math.max(0, innerHeight - (viewport?.height || innerHeight) - (viewport?.offsetTop || 0));
      this.host.style.setProperty('--kw-left', left + 'px');
      this.host.style.setProperty('--kw-width', width + 'px');
      this.host.style.setProperty('--kw-bottom', (keyboard + gap) + 'px');
      this.host.style.setProperty('--kw-height', Math.max(160, (viewport?.height || innerHeight) - gap * 2) + 'px');
    }
    sync(root) {
      if (!this.host) return;
      const panel = this.host.shadowRoot.querySelector('[role="dialog"][data-sizing="form-wrapper"]');
      if (!panel || (panel.tagName === 'DIALOG' && !panel.open) || root?.dataset.currentStep === 'tease') {
        this.panel = null;
        this.closing = false;
        return;
      }
      const form = panel.querySelector('[data-testid="form"]');
      if (form && this.copy) {
        const header = panel.querySelector('[class*="_formHeader_"]');
        const heading = header?.querySelector('h2');
        const disclaimer = panel.querySelector('[class*="_formDisclaimer_"]');
        const submit = form.querySelector('[data-testid="btn-form-submit"]');
        for (const [element, key] of [[heading, 'title'], [disclaimer, 'privacy']]) {
          const text = this.copy.content.querySelector('[data-copy="' + key + '"]')?.textContent;
          if (element && text && element.textContent !== text) element.textContent = text;
        }
        if (heading && panel.getAttribute('aria-label') !== heading.textContent) panel.setAttribute('aria-label', heading.textContent);
        if (header && !header.querySelector('[data-copy="eyebrow"]')) header.prepend(this.copy.content.querySelector('[data-copy="eyebrow"]').cloneNode(true));
        const buttonText = this.copy.content.querySelector('[data-copy="button"]')?.textContent;
        if (submit && submit.getAttribute('aria-disabled') !== 'true' && !submit.querySelector('svg') && buttonText && submit.textContent !== buttonText) submit.textContent = buttonText;
        for (const [selector, autocomplete] of [['[data-testid="field-first_name"]', 'given-name'], ['[data-testid="field-email"]', 'email']]) {
          const input = form.querySelector(selector);
          if (input && input.autocomplete !== autocomplete) input.autocomplete = autocomplete;
          if (input) {
            input.placeholder = ' ';
            const label = (input.id && form.querySelector('label[for="' + CSS.escape(input.id) + '"]')) || input.parentElement.querySelector('[class*="_formInputFieldLabel_"]');
            if (label) {
              if (!input.getAttribute('aria-label')) input.setAttribute('aria-label', label.textContent.trim());
              const updateLabel = () => label.setAttribute('data-kindra-field-filled', String(input.value.length > 0));
              updateLabel();
              if (!input.hasAttribute('data-kindra-label-ready')) {
                input.setAttribute('data-kindra-label-ready', '');
                input.addEventListener('input', updateLabel);
                input.addEventListener('change', updateLabel);
              }
            }
          }
        }
      }
      this.syncSuccess(panel, root);
      if (this.panel === panel) return;
      this.panel = panel;
      this.position();
      this.enter();
    }
    syncSuccess(panel, root) {
      // Only Shopify Forms' confirmed success view can reveal the offer.
      if (root?.dataset.currentStep !== 'success' || !this.copy) return;
      const banner = panel.querySelector('[data-testid="banner-success"]');
      if (!banner) return;
      const nativeCode = banner.querySelector('[class*="_copyToClipboardText_"]')?.textContent.trim();
      const configured = this.copy.content.querySelector('[data-copy="offer-code"]');
      const matchingForm = this.host.id === 'app-embed-container-' + configured?.dataset.formId;
      const code = nativeCode || (matchingForm ? configured?.textContent.trim() : '');
      if (!code) return;
      const heading = banner.querySelector('h2');
      const body = banner.querySelector(':scope > [class*="_textBody_"]');
      for (const [element, key] of [[heading, 'success-title'], [body, 'success-body']]) {
        const text = this.copy.content.querySelector('[data-copy="' + key + '"]')?.textContent;
        if (element && text && element.textContent !== text) element.textContent = text;
      }
      if (heading && panel.getAttribute('aria-label') !== heading.textContent) panel.setAttribute('aria-label', heading.textContent);
      let card = banner.querySelector('[data-kindra-welcome-code]');
      if (!card) {
        card = this.copy.content.querySelector('[data-kindra-welcome-code]').cloneNode(true);
        const button = card.querySelector('[data-copy-welcome-code]');
        const status = card.querySelector('[data-copy-status]');
        button.addEventListener('click', async () => {
          try {
            await navigator.clipboard.writeText(card.querySelector('[data-welcome-code]').textContent);
            status.textContent = status.dataset.copied;
          } catch (_) {
            status.textContent = status.dataset.failed;
          }
        });
        banner.append(card);
      }
      const label = card.querySelector('[data-welcome-code]');
      if (label.textContent !== code) label.textContent = code;
      const nativeButton = banner.querySelector('[class*="_copyToClipboardButton_"]');
      if (nativeButton) nativeButton.hidden = true;
    }
    frames() {
      return [
        { transform: 'translateY(8px) scale(.985)', opacity: 0 },
        { transform: 'translateY(0) scale(1)', opacity: 1 }
      ];
    }
    enter() {
      if (this.motion.matches || !this.panel.animate) return;
      this.animation?.cancel();
      this.animation = this.panel.animate(this.frames(), { duration: 200, easing: 'cubic-bezier(.2, .7, .2, 1)' });
    }
    async close() {
      if (this.closing || !this.panel) return;
      const button = this.panel.querySelector('[aria-label="Close modal"]');
      if (!button) return;
      this.closing = true;
      const current = getComputedStyle(this.panel);
      const start = { transform: current.transform, opacity: current.opacity };
      this.animation?.cancel();
      if (!this.motion.matches && this.panel.animate) {
        this.animation = this.panel.animate([start, this.frames()[0]], { duration: 140, easing: 'ease-out', fill: 'forwards' });
        try { await this.animation.finished; } catch (_) {}
      }
      if (this.editorOpen) {
        this.panel.close();
        this.animation?.cancel();
        this.panel = null;
        this.editorOpen = false;
        this.closing = false;
        this.tray.sync();
        this.tray.offerTrigger?.focus({ preventScroll: true });
        return;
      }
      this.allowClose = true;
      button.click();
      this.allowClose = false;
      this.closing = false;
      this.tray.schedule();
    }
    destroy() {
      this.animation?.cancel();
      this.abort.abort();
      this.hostAbort?.abort();
      this.editorHost?.remove();
    }
  };
})();


